import React, { HTMLAttributes, memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AiAgentDocumentSourceItem } from '@lgg/isomorphic/types/__generated__/graphql';
import { TableColumns, TableProps } from 'src/components/general/display/table';
import { getDefaultSortOrder, TableSortData } from 'src/components/general/table-helpers';
import { FlexColumn } from 'src/components/layout/flex-column';
import {
  getCreatedAtColumn,
  getDeleteRowOption,
  getRowOptionsColumn,
  KnowledgeSourceStatusTag,
  KnowledgeSourceTable,
  KnowledgeSourceTableFrame,
  TableLightText,
} from 'src/components/pages/ai-agents/components/ai-agents-table-columns';
import { formatBytes } from 'src/components/pages/ai-agents/knowledge-base-sources/websites/helpers';
import { useAuthorization } from 'src/hooks/use-authorization';

const FileCell = styled(FlexColumn)`
  align-items: flex-start;
  gap: 3px;
`;

const FileName = styled.span`
  color: ${({ theme }) => theme.colors.smalt};
  font-size: 13px;
  line-height: 16px;
  overflow-wrap: anywhere;
`;

type DocumentSourcesTableProps = {
  data: AiAgentDocumentSourceItem[];
  sortData: TableSortData;
  onChange: TableProps<AiAgentDocumentSourceItem>['onChange'];
  onDelete: (document: AiAgentDocumentSourceItem) => void;
};

export const DocumentSourcesTable = memo<DocumentSourcesTableProps>(
  ({ data, sortData, onChange, onDelete }) => {
    const { t } = useTranslation(['aiAgents', 'common']);
    const { hasPermission } = useAuthorization();
    const canDelete = hasPermission('ai:company.ai_agent_knowledge_base:delete:all');

    const columns: TableColumns<AiAgentDocumentSourceItem>[] = [
      {
        title: t('aiAgents:knowledge.documents.columnHeaders.file'),
        key: 'title',
        showSorterTooltip: false,
        sorter: true,
        defaultSortOrder: getDefaultSortOrder(
          sortData.key === 'title',
          sortData.direction,
        ),
        render: (_, document) => (
          <FileCell>
            <FileName data-lgg-id="document-sources-cell-file">{document.title}</FileName>
            <KnowledgeSourceStatusTag
              status={document.status}
              label={t(`aiAgents:knowledge.documents.status.${document.status}`)}
              data-lgg-id="document-status-badge"
            />
          </FileCell>
        ),
      },
      {
        title: t('aiAgents:knowledge.documents.columnHeaders.size'),
        key: 'size',
        width: '97px',
        render: (_, { size }) => (
          <TableLightText data-lgg-id="document-sources-cell-size">
            {typeof size === 'number' ? formatBytes(size) : ''}
          </TableLightText>
        ),
      },
      getCreatedAtColumn<AiAgentDocumentSourceItem>({
        title: t('aiAgents:knowledge.documents.columnHeaders.addedTime'),
        sortData,
        testId: 'document-sources-cell-added',
        width: '142px',
      }),
      ...(canDelete
        ? [
            getRowOptionsColumn<AiAgentDocumentSourceItem>((document) => [
              getDeleteRowOption({
                label: t('common:delete'),
                testId: 'document-option-delete',
                onClick: () => onDelete(document),
              }),
            ]),
          ]
        : []),
    ];

    return (
      <KnowledgeSourceTableFrame>
        <KnowledgeSourceTable
          rowKey={({ id }) => id}
          data-lgg-id="document-sources-table"
          columns={columns}
          tableLayout="fixed"
          loading={false}
          dataSource={data}
          pagination={false}
          showHeader
          scroll={{ x: 700 }}
          onRow={() =>
            ({
              'data-lgg-id': 'document-sources-table-row',
            } as HTMLAttributes<HTMLElement>)
          }
          onChange={onChange}
        />
      </KnowledgeSourceTableFrame>
    );
  },
);
